import { useEffect, useRef, useState } from "react";
import { SheetDialog, TextPad } from "../../components/shared";
import { blurOtherField } from "../../components/shared/padFocus";
import { api } from "../../api/client";
import { ApiException } from "../../api/types";
import d from "./dashActions.module.css";
import s from "./sidePlayer.module.css";

/**
 * Отчёт друг о друге без отчёта об игре (13.10, владелец: «у ОО возможность запросить отчёты …
 * у играющих операторов, отчёты друг о друге»). Для тех, кто на брони не назначен: игроки
 * учебной игры, оператор с другой игры филиала. Назначенные пишут в отчёте игры (GameReportModal).
 *
 * По полю на каждого, о ком попросил ОО; затравка «Имя — », как в отчёте игры, — голая затравка
 * отзывом не считается. Сдавать можно по одному. Отправка ждёт сервер: «не запрашивали» (403)
 * и «откроется за 30 минут» (409) — текстом в шторке.
 */
export interface PeerSubject { id: string | null; name: string; done: boolean; activity: string; impression: string }
export interface PeerGame { bookingId: number; quest: string; time: string; branch: string; learning: boolean; open: boolean; subjects: PeerSubject[] }

const keyOf = (x: PeerSubject) => x.id ?? "any";
/* та же проверка, что в GameReportModal: пусто или нетронутое «Имя —» — не отзыв */
const wrote = (x: PeerSubject, text: string) => { const body = text.trim(); return body !== "" && body !== `${x.name} —`; };
const filled = (x: PeerSubject, text: string) => wrote(x, text) || x.impression.trim() !== "";

export function PeerOnlySheet({ game, onClose, onDone }: { game: PeerGame | null; onClose: () => void; onDone: () => void }) {
  const [txt, setTxt] = useState<Record<string, string>>({});
  const [pad, setPad] = useState<string | null>(null); /* чьё поле сейчас пишет наша клавиатура */
  const [dockH, setDockH] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [invalid, setInvalid] = useState(false);
  const hostRef = useRef<HTMLTextAreaElement | null>(null);
  const bid = game?.bookingId ?? null;
  /* закрытая шторка ещё уезжает вниз — до конца анимации показываем последнюю игру, а не пустоту */
  const last = useRef<PeerGame | null>(null);
  if (game) last.current = game;
  const g = game ?? last.current;

  /* На каждое открытие — уже сданное (можно переписать) или затравка «Имя — ». По bookingId,
     а не по объекту: перечитка списка под открытой шторкой не должна стирать набранное. */
  useEffect(() => {
    if (!game) return;
    setTxt(Object.fromEntries(game.subjects.map((x) => [keyOf(x), x.done && x.activity.trim() ? x.activity : `${x.name} — `])));
    setPad(null); setDockH(0); setBusy(false); setErr(null); setInvalid(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bid]);

  /* Шит поднялся над клавиатурой — докручиваем поле к себе (как в ExtraReportSheet). */
  useEffect(() => {
    if (!dockH) return;
    const t = window.setTimeout(() => hostRef.current?.scrollIntoView({ block: "center", behavior: "smooth" }), 220);
    return () => window.clearTimeout(t);
  }, [dockH, pad]);

  const put = (k: string, v: string) => {
    setTxt((p) => ({ ...p, [k]: v }));
    if (invalid || err) { setInvalid(false); setErr(null); }
  };
  const dismiss = () => { if (!busy) { setPad(null); onClose(); } };

  const send = async () => {
    if (!game || busy) return;
    const subs = game.subjects.map((x) => ({ x, text: txt[keyOf(x)] ?? "" }));
    if (!subs.some(({ x, text }) => filled(x, text))) {
      setInvalid(true); setErr("Напиши хотя бы об одном — одно «Имя —» отзывом не считается.");
      return;
    }
    /* уже сданное и не тронутое заново не шлём; голую затравку — тоже (старый отзыв с одним «впечатлением» не затираем) */
    const todo = subs.filter(({ x, text }) => wrote(x, text) && !(x.done && text === x.activity));
    setBusy(true); setErr(null);
    let i = 0;
    try {
      for (; i < todo.length; i++) {
        const { x, text } = todo[i]!;
        await api("/api/peer-review/report", { method: "POST", body: { booking_id: game.bookingId, subject_id: x.id, activity: text, impression: x.impression } });
      }
      setPad(null); onDone();
    } catch (e: unknown) {
      /* текст сервера (403/409) — как есть, обрыв связи — по-русски; полей несколько — о ком не ушло */
      const msg = e instanceof ApiException && e.body?.error ? e.body.error : "Не удалось отправить — проверь связь и попробуй ещё раз";
      const who = game.subjects.length > 1 ? todo[i]?.x.name : undefined;
      setErr(who ? `${who}: ${msg}` : msg);
      setBusy(false);
    }
  };

  const padSubj = game?.subjects.find((x) => keyOf(x) === pad) ?? null; /* шторка закрыта — клавиатуры нет */
  return (
    <SheetDialog open={!!game} onDismiss={dismiss} ariaLabel="Отчёт друг о друге" maxHeight="76dvh" liftBy={dockH}>
      <div className={d.sheetHead}>
        <span className={`${d.sheetTitle} ${s.tCyan}`}>✍ Отчёт друг о друге</span>
        <span className={d.sheetDate}>{g ? `${g.quest} · ${g.time}` : ""}</span>
      </div>
      <div className={d.sheetBody}>
        <div className={d.hint}>
          {g?.learning ? "Учебная игра. " : ""}Напиши о каждом, о ком попросил ОО: что делал(а), как справился(ась). Можно сдавать по одному.
        </div>
        {(g?.subjects ?? []).map((x) => {
          const k = keyOf(x), v = txt[k] ?? "";
          return (
            <div key={k} className={d.field}>
              <div className={s.who}>{x.name}{x.done ? " · сдано ✓" : ""}</div>
              <textarea className={`${d.textarea} ${invalid && !filled(x, v) ? s.invalid : ""}`} value={v} inputMode="none" readOnly={pad !== k}
                data-pad-host="" placeholder={`${x.name} — выскажи мнение: что делал(а), как справился(ась)…`}
                onChange={(e) => put(k, e.target.value)}
                onFocus={(e) => { hostRef.current = e.currentTarget; blurOtherField(e.currentTarget); setPad(k); }}
                onPointerDown={(e) => { hostRef.current = e.currentTarget; setPad(k); }} />
            </div>
          );
        })}
        {err ? <div className={d.err}>{err}</div> : null}
        {/* одна клавиатура на все поля: key — пересоздать под новое поле, правки старого она дофлашит сама */}
        {pad && padSubj ? (
          <TextPad key={pad} open docked hostRef={hostRef} multiline numRow label={padSubj.name}
            value={txt[pad] ?? ""} onChange={(v) => put(pad, v)} onDockHeight={setDockH} onClose={() => setPad(null)} />
        ) : null}
      </div>
      <div className={d.foot}>
        <button type="button" className={`${d.btn} ${d.btnCancel}`} onClick={dismiss} disabled={busy}>Отмена</button>
        <button type="button" className={`${d.btn} ${d.btnGo}`} onClick={() => void send()} disabled={busy}>
          {busy ? "Отправляю…" : "Сдать"}
        </button>
      </div>
    </SheetDialog>
  );
}
